import { type JSX, type ReactNode, type Ref } from 'react';
import type { RenditionDto, UsageDto } from '../api/library.js';
import { useI18n } from '../i18n/context.js';

/**
 * Карточка библиотеки (01-SPEC.md §5.10): видно, в скольких книгах объект
 * используется и в скольких стилях уже нарисован. Удаление запрещено, пока
 * объект в живой книге, — остаётся архивация.
 */
export function LibraryCard({
  title,
  subtitle,
  previewUrl,
  usage,
  renditions,
  extra,
  open,
  blocked,
  onToggle,
  onArchive,
  onDelete,
  elementRef,
  children,
}: {
  title: string;
  subtitle?: string;
  previewUrl?: string;
  usage: UsageDto;
  renditions: readonly RenditionDto[];
  extra?: ReactNode;
  open: boolean;
  blocked: boolean;
  onToggle(): void;
  onArchive(): void;
  onDelete(): void;
  elementRef?: Ref<HTMLLIElement>;
  children: ReactNode;
}): JSX.Element {
  const { t } = useI18n();
  const drawn = renditions.filter((rendition) => rendition.variants.length > 0).length;

  return (
    <li ref={elementRef} className="kitup-card p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          {previewUrl ? (
            <img src={previewUrl} alt="" className="h-16 w-16 rounded object-cover" />
          ) : null}
          <div>
            <button
              type="button"
              className="text-left font-semibold"
              aria-expanded={open}
              onClick={onToggle}
            >
              {title}
            </button>
            {subtitle ? (
              <p className="text-sm text-[var(--kitup-color-muted)]">{subtitle}</p>
            ) : null}
            <p className="text-sm text-[var(--kitup-color-muted)]">
              {usage.books === 0 ? t.library.usage.unused : t.library.usage.inBooks(usage.books)}
              {' · '}
              {t.library.usage.styles(drawn)}
            </p>
            {extra}
          </div>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <button type="button" className="kitup-quiet-button" onClick={onArchive}>
            {t.library.actions.archive}
          </button>
          {/*
            A disabled Delete with a standing orange warning read as an error nobody made.
            While the item is in an active book, Delete is simply not offered, and the reason
            is a quiet note.
          */}
          {blocked ? null : (
            <button type="button" className="kitup-quiet-button text-red-700" onClick={onDelete}>
              {t.library.actions.delete}
            </button>
          )}
        </div>
      </div>
      {blocked ? (
        <p className="mt-2 text-xs text-[var(--kitup-color-muted)]">
          {t.library.usage.blockedDelete}
        </p>
      ) : null}
      {open ? <div className="mt-4">{children}</div> : null}
    </li>
  );
}
